import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useEffect, useRef, useState } from "react";
import CentresAndUsers from "..";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
  ITEMS_PER_PAGE_OPTIONS,
  MAX_VISIBLE_PAGES,
  STATUS_OPTIONS,
  USER_SORT_OPTIONS,
} from "../../../../constants/app";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
  getAllActiveCentresThunk,
  getCentresUsersThunk,
  otherRequestsThunk,
} from "../../../../store/superadmin/thunks";
import {
  TCenterData,
  TCenterDetails,
} from "../../../../store/superadmin/types";
import { capitalizeName } from "../../../../utils/stringUtils";
import Pagination from "../../../Pagination/Pagination";
import Users from "../../../PopupModal/Users";
import SaveNotification from "../../../SaveNotification";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Block from "../../../UI/Icons/Block";
import Chevron from "../../../UI/Icons/Chevron";
import ChevronSmall from "../../../UI/Icons/ChevronSmall";
import EditPencil from "../../../UI/Icons/EditPencil";
import Mail from "../../../UI/Icons/Mail";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Search from "../../../UI/Icons/Search";
import VerticalDots from "../../../UI/Icons/VerticalDots";
import SortBySelect from "../../../UI/Inputs/SortBySelect";
import TextInput from "../../../UI/Inputs/TextInput";

const CentreUsers: FC = () => {
  const dispatch = useDispatch();

  const centreAdmins = useSelector((state) => state.superadmin.centre_admins);
  const centreUsers = useSelector((state) => state.superadmin.centre_users);
  const centreAdminsPagination = useSelector(
    (state) => state.superadmin.allCenterList
  );
  const totalCentreAdminCount = useSelector(
    (state) => state.superadmin.totalCentreAdminCount
  );
  const totalCentreUsersCount = useSelector(
    (state) => state.superadmin.totalCentreUserCount
  );
  const allActiveCentresList = useSelector(
    (state) => state.superadmin.allActiveCentres
  );

  const [editCentreAdminId, setEditCentreAdminId] = useState<number | null>(0);
  const [editingUser, setEditingUser] = useState<number>(0);
  const [isOpen1, setIsOpen1] = useState<boolean>(true);
  const [isOpen2, setIsOpen2] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const isInitialLoad = useRef<boolean>(true);
  const [selectedCentre, setSelectedCentre] = useState<number[]>([]);
  const [selectedKeyword, setSelectedKeyword] = useState<string>("");
  const [editValues, setEditValues] = useState<any>(null);
  const [openCentreFilterDropdown, setOpenCentreFilterDropdown] = useState<
    number | null
  >(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("active");
  const [centreAdminPage, setCentreAdminPage] = useState<number>(1);
  const [totalCentreAdminPages, setTotalCentreAdminPages] = useState<number>(1);
  const [centreAdminPaginationLinks, setCentreAdminPaginationLinks] = useState<
    any[]
  >([]);
  const [centreAdminPerPageList, setCentreAdminPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [centreUsersPage, setCentreUsersPage] = useState<number>(1);
  const [totalCentreUsersPages, setTotalCentreUsersPages] = useState<number>(1);
  const [centreUsersPaginationLinks, setCentreUsersPaginationLinks] = useState<
    any[]
  >([]);
  const [centreUsersPerPageList, setCentreUsersPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [searchInput, setSearchInput] = useState("");
  const [selectedCentreAdminSort, setSelectedCentreAdminSort] =
    useState<string>("first_name-asc");
  const [selectedCentreAdminSortBy, setSelectedCentreAdminSortBy] =
    useState<string>("first_name");
  const [selectedCentreAdminSortOrder, setSelectedCentreAdminSortOrder] =
    useState<string>("asc");
  const [selectedCentreUserSort, setSelectedCentreUserSort] =
    useState<string>("first_name-asc");
  const [selectedCentreUserSortBy, setSelectedCentreUserSortBy] =
    useState<string>("first_name");
  const [selectedCentreUserSortOrder, setSelectedCentreUserSortOrder] =
    useState<string>("asc");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  const handleSelectFilter = (selectedOption: any) => {};

  const handleFilterStatus = (selectedOption: any) => {
    setSelectedStatus(selectedOption.value);
    setCentreAdminPage(1);
    setCentreUsersPage(1);
  };

  /**
   * Handles the creation of a new user by toggling the modal state and resetting the selected user detail.
   * This function is triggered when the user clicks on the "Create User" button.
   */
  const handleCreateUser = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(getAllActiveCentresThunk({ paginate: false }));
    dispatch(hideTransparentPreloaderAction());
    setShowModal(!false);
  };

  /**
   * Converts an array of centre IDs to their corresponding business names.
   *
   * @param ids - An array of numeric centre IDs.
   * @param centres - An array of centre objects containing their details.
   * @returns A string of comma-separated business names for the given centre IDs.
   */
  const convertIdsToBusinessNames = (
    ids: number[],
    centers: Array<TCenterDetails>
  ): string => {
    return ids
      .map((id) => centers.find((center) => center.id === id)?.business_name)
      .filter(Boolean)
      .join(", ");
  };

  const handleSelect = (selectedOption: any) => {};

  const handleSorting = (type: string, opt?: any): void => {
    let splitValue = opt.value.split("-");
    if (type === "centre_admin") {
      setSelectedCentreAdminSort(opt);
      setSelectedCentreAdminSortBy(splitValue[0]);
      setSelectedCentreAdminSortOrder(splitValue[1]);
    } else if (type === "centre_user") {
      setSelectedCentreUserSort(opt);
      setSelectedCentreUserSortBy(splitValue[0]);
      setSelectedCentreUserSortOrder(splitValue[1]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const filterMenu = document.querySelector(".filter-menu");
      if (
        filterMenu &&
        !filterMenu.contains(event.target as Node) &&
        openDropdown !== null
      ) {
        setOpenCentreFilterDropdown(0); // Close the dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown, openCentreFilterDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideEditBtn = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideEditBtn);

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideEditBtn);
    };
  }, []);

  const handleEdit = (user: TCenterData) => {
    setShowModal(!false);
    setOpenDropdown(false);
    setEditingUser(user?.id);
    setEditValues(user);
  };

  /**
   * Fetches a list of users based on the provided parameters.
   *
   * @param page - The current page number for pagination.
   * @param keyword - A search keyword to filter the users.
   * @param centre - An array of centre IDs to filter the users by their associated centres.
   * @param role - An array of role IDs to filter the users by their roles.
   * @returns A promise that dispatches an action to fetch the users.
   */
  const fetchUsers = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCentresUsersThunk({
        keyword: selectedKeyword,
        centre_admin_page: centreAdminPage,
        centre_user_page: centreUsersPage,
        centre_admin_per_page: centreAdminPerPageList,
        centre_user_per_page: centreUsersPerPageList,
        status: selectedStatus,
        centre_id: selectedCentre,
        centre_admin_sort_by: selectedCentreAdminSortBy,
        centre_admin_sort_order: selectedCentreAdminSortOrder,
        centre_user_sort_by: selectedCentreUserSortBy,
        centre_user_sort_order: selectedCentreUserSortOrder,
      })
    );
    await dispatch(getAllActiveCentresThunk({ paginate: false }));
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    fetchUsers();
  }, [
    centreAdminPerPageList,
    centreAdminPage,
    selectedStatus,
    selectedCentre,
    centreUsersPerPageList,
    centreUsersPage,
    selectedKeyword,
    selectedCentreAdminSortBy,
    selectedCentreAdminSortOrder,
    selectedCentreUserSortBy,
    selectedCentreUserSortOrder,
  ]);

  useEffect(() => {
    if (centreAdminsPagination !== null) {
      setCentreAdminPaginationLinks(
        centreAdminsPagination?.centre_admin?.links
      );
      setTotalCentreAdminPages(centreAdminsPagination?.centre_admin?.last_page);

      setCentreUsersPaginationLinks(centreAdminsPagination?.centre_user?.links);
      setTotalCentreUsersPages(centreAdminsPagination?.centre_user?.last_page);
    }
  }, [centreAdminsPagination]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param url - The numeric value representing the number of items per page.
   */
  const handleCentreAdminPageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("centre_admin_page");
      setCentreAdminPage(Number(page));
    }
  };

  const handleCentreUserPageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("centre_user_page");
      setCentreUsersPage(Number(page));
    }
  };

  /**
   * Handles changes to the centre filter selection.
   *
   * @param value - The numeric value representing the selected centre.
   *                A value of 0 indicates no selection.
   *
   * - If the value is not 0 and does not already exist in the `selectedCentre` array:
   *   - Sets `isInitialLoad.current` to `false`.
   *   - Updates the `selectedCentre` state by appending the new value to the existing array.
   */
  const handleCentreFilterChange = (value: number) => {
    // setOpenCentreFilterDropdown(0); // Commented for the solution - https://enoahinc.atlassian.net/browse/ACC-299
    if (value !== 0) {
      // Check if the value already exists in the array
      if (!selectedCentre.includes(value)) {
        isInitialLoad.current = false;
        setSelectedCentre((prevValue: Array<number>) => {
          return [...prevValue, value];
        });
      }
    }
  };

  /**
   * Toggles the state of a dropdown menu based on the provided index.
   * If the dropdown corresponding to the given index is already open, it will be closed.
   * Otherwise, it will open the dropdown for the specified index and close any other open dropdowns.
   *
   * @param index - The index of the dropdown to toggle.
   */
  const toggleCentreFIlterDropdown = (index: number) => {
    setOpenCentreFilterDropdown(
      openCentreFilterDropdown === index ? null : index
    );
  };

  // Trigger API only when debounced value updates
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setSelectedKeyword(debouncedSearch);
      setCentreAdminPage(1);
      setCentreUsersPage(1);
    }
  }, [debouncedSearch]);

  const makeUserUpdate = async (id: string, action: string) => {
    const payload = {
      user_uuid: id,
      action: action,
    };
    setOpenDropdown(false);
    dispatch(showTransparentPreloaderAction());
    const result = await dispatch(otherRequestsThunk(payload));
    if (isFulfilled(result)) {
      await fetchUsers();
      dispatch(hideTransparentPreloaderAction());
      if (action === "resend_invite") {
        dispatch(showNotificationAction("Invite Resent"));
      } else if (action === "cancel_invite") {
        dispatch(showNotificationAction("Invite Cancelled"));
      } else if (action === "make_active") {
        dispatch(showNotificationAction("User Activated"));
      } else if (action === "make_inactive") {
        dispatch(showNotificationAction("User Inactivated"));
      }
    }
  };

  const setSearchUserValue = (value: string) => {
    setSearchInput(value);
    setSelectedCentreAdminSort("first_name-asc"); // Reset sorting to default
    setSelectedCentreAdminSortBy("first_name");
    setSelectedCentreAdminSortOrder("asc");
  };

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={editingUser === 0 ? "Invite Sent" : "Saved"}
          onClose={() => setShowNotification(false)}
        />
      )}

      {/** Popup for Add/Edit Centre user */}
      <Users
        editingUser={editingUser}
        showModal={showModal}
        setShowModal={setShowModal}
        setShowNotification={setShowNotification}
        user={"admin"}
        editValues={editValues}
        setEditingUser={setEditingUser}
        setEditValues={setEditValues}
        fetchUsers={fetchUsers}
      />
      {/** End - Popup for Add/Edit Centre user */}

      <div className="d-flex flex-column gap-20">
        <CentresAndUsers create={"users"} handleCreateUser={handleCreateUser} />
        <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center justify-content-lg-between">
          <div className="form-group w-200px mobile-w-100 input-icon icon-left">
            <Search />
            <TextInput
              field={"search"}
              label={"Search..."}
              onChange={(e) => setSearchUserValue(e.target.value)}
              // onChange={(e) => e.target.value?.length > 2 && setSelectedKeyword(e.target.value)}
              placeholder={" " + ""}
            />
          </div>

          <div className="d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-lg-center">
            <div className="position-relative">
              <div
                className="d-flex gap-3 align-items-center cursor-pointer"
                onClick={() => toggleCentreFIlterDropdown(1)}
              >
                <div className="fs-13 fw-400 text-black">Centres</div>
                <div className="fs-13 fw-700 text-black">
                  {/* {selectedCentre.length > 0
                    ? convertIdsToBusinessNames(selectedCentre, filteredCentres)
                    : "All"} */}
                  {selectedCentre.length > 0
                    ? selectedCentre.length + " centres selected"
                    : "All"}
                </div>

                <ChevronSmall isOpen={true} />
              </div>
              {openCentreFilterDropdown === 1 && (
                <div className="position-absolute end-0 d-flex flex-column dropdown-menu w-266px">
                  <div className="filter-menu">
                    {allActiveCentresList &&
                      allActiveCentresList !== null &&
                      allActiveCentresList.map((center: TCenterDetails) => {
                        return (
                          <div
                            className="theme-check dropdown-item"
                            key={center.id}
                          >
                            <input
                              type="checkbox"
                              className="theme-check-input"
                              id={`checkbox_${center.id}`}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleCentreFilterChange(center.id);
                                } else {
                                  isInitialLoad.current = false;
                                  setSelectedCentre((prevValue) =>
                                    prevValue.filter((id) => id !== center.id)
                                  );
                                }
                              }}
                              checked={selectedCentre.includes(center.id)}
                            />
                            <label
                              htmlFor={`checkbox_${center.id}`}
                              className="theme-check-label fs-16 text-truncate"
                            >
                              {center.business_name}
                            </label>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            <div className="d-flex gap-3 align-items-center position-relative">
              <div className="fs-13 text-black text-nowrap">Status</div>
              <div className="form-group">
                <SortBySelect
                  options={STATUS_OPTIONS}
                  placeholder="Select..."
                  enableFilter={false}
                  defaultValue={STATUS_OPTIONS[1]}
                  onChange={handleFilterStatus}
                  onSelect={handleSelectFilter}
                />
                {/* <select
                  name=""
                  id=""
                  className="form-select text-only fs-13 fw-700"
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                  }}
                >
                  <option value="">All</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select> */}
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex flex-column gap-10">
          <div className="position-relative bg-teal-light py-8px px-15px radius-8px d-flex flex-column flex-lg-row gap-8 align-items-center justify-content-lg-between">
            <div
              className="d-flex gap-8 align-items-center cursor-pointer"
              onClick={() => setIsOpen1(!isOpen1)}
            >
              <Chevron isOpen={isOpen1} />
              <div className="fs-16 fw-700 text-black">
                {totalCentreAdminCount} Centre Admin
              </div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div className="fs-13 text-black text-nowrap">Sort By</div>
              <div className="form-group">
                <SortBySelect
                  // className="form-select text-only min-h-auto fw-700"
                  options={USER_SORT_OPTIONS}
                  onChange={(selectedValue) =>
                    handleSorting("centre_admin", selectedValue)
                  }
                  enableFilter={false}
                  // value={selectedCentreUserSort}
                  defaultValue={USER_SORT_OPTIONS[0]}
                  onSelect={handleSelect}
                />
                {/* <SelectInput
                  className="form-select text-only min-h-auto fw-700"
                  options={options}
                  onChange={(selectedValue) =>
                    handleSorting("centre_admin", selectedValue)
                  }
                  value={selectedCentreAdminSort}
                /> */}
              </div>
            </div>
          </div>
          {isOpen1 && (
            <>
              <div className="d-flex flex-column">
                {centreAdmins && centreAdmins?.length > 0 ? (
                  centreAdmins.map(
                    (centreAdmin: TCenterData, index: number) => (
                      <div
                        key={index}
                        className="bg-white p-15px d-flex gap-20 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black"
                      >
                        <div
                          className={`d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-start align-items-lg-center text-break ${
                            centreAdmin.status === "active"
                              ? " text-black"
                              : "text-grey-medium"
                          }`}
                        >
                          <div className="min-w-200px w-200px fs-16 fw-700">
                            {capitalizeName(centreAdmin?.first_name ?? "")} {capitalizeName(centreAdmin?.last_name ?? "")}
                          </div>
                          <div className="min-w-200px w-200px">
                            {centreAdmin?.email}
                          </div>
                          <div className="min-w-120px">
                            {centreAdmin?.phone_number}
                          </div>
                          <div className="min-w-120px">
                            {centreAdmin.email_verified_at === null &&
                            centreAdmin.status === "active" ? (
                              <span className="text-teal-dark fs-13 fw-700">
                                Invitation Pending
                              </span>
                            ) : centreAdmin.status === "inactive" ? (
                              <span className="text-grey-medium fs-13 fw-700">
                                Inactive
                              </span>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                        <div className="position-relative">
                          <CustomButton
                            variant={"primary"}
                            className="btn-theme bg-white p-0 h-auto"
                            onClick={() => {
                              setOpenDropdown(!openDropdown);
                              setEditCentreAdminId(centreAdmin?.id);
                            }}
                          >
                            <VerticalDots />
                          </CustomButton>
                          {openDropdown &&
                            editCentreAdminId === centreAdmin?.id && (
                              <div
                                className="position-absolute end-0 d-flex flex-column dropdown-menu"
                                ref={dropdownRef} // Attach ref to handle click outside detection
                              >
                                {centreAdmin.email_verified_at !== null &&
                                centreAdmin.status === "active" ? (
                                  <>
                                    <div
                                      className="dropdown-item cursor-pointer"
                                      onClick={() => handleEdit(centreAdmin)}
                                    >
                                      <EditPencil />
                                      Edit User
                                    </div>
                                    <div
                                      className="dropdown-item cursor-pointer text-nowrap"
                                      onClick={() =>
                                        makeUserUpdate(
                                          centreAdmin.uuid,
                                          "make_inactive"
                                        )
                                      }
                                    >
                                      <Block />
                                      Make Inactive
                                    </div>
                                  </>
                                ) : centreAdmin.email_verified_at === null &&
                                  centreAdmin.status === "active" ? (
                                  <>
                                    <div
                                      className="dropdown-item cursor-pointer text-nowrap"
                                      onClick={() =>
                                        makeUserUpdate(
                                          centreAdmin.uuid,
                                          "resend_invite"
                                        )
                                      }
                                    >
                                      <Mail />
                                      Resend Invite
                                    </div>
                                    <div
                                      className="dropdown-item cursor-pointer text-nowrap"
                                      onClick={() =>
                                        makeUserUpdate(
                                          centreAdmin.uuid,
                                          "cancel_invite"
                                        )
                                      }
                                    >
                                      <Block />
                                      Cancel Invite
                                    </div>
                                  </>
                                ) : centreAdmin.status === "inactive" ? (
                                  <>
                                    <div
                                      className="dropdown-item cursor-pointer text-nowrap"
                                      onClick={() =>
                                        makeUserUpdate(
                                          centreAdmin.uuid,
                                          "make_active"
                                        )
                                      }
                                    >
                                      <RoundedTick />
                                      Make Active
                                    </div>
                                  </>
                                ) : (
                                  ""
                                )}{" "}
                              </div>
                            )}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="d-flex flex-column align-items-center text-grey-medium">
                    No Centre Admin found
                  </div>
                )}
              </div>

              {totalCentreAdminCount > DEFAULT_ITEMS_PER_PAGE && (
                <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="fs-13 text-black text-nowrap">
                      Items per page
                    </div>
                    <div className="form-group">
                      <select
                        name=""
                        id=""
                        className="form-select text-only"
                        onChange={(e) => {
                          isInitialLoad.current = false;
                          setCentreAdminPerPageList(Number(e.target.value));
                          setCentreAdminPage(1);
                        }}
                      >
                        {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                          return (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <Pagination
                    currentPage={centreAdminPage}
                    totalPages={totalCentreAdminPages}
                    paginationLinks={centreAdminPaginationLinks}
                    maxVisiblePages={MAX_VISIBLE_PAGES}
                    onPageChange={handleCentreAdminPageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="d-flex flex-column gap-10">
          <div className="position-relative bg-teal-light py-8px px-15px radius-8px d-flex flex-column flex-lg-row gap-8 align-items-center justify-content-lg-between">
            <div
              className="d-flex gap-8 align-items-center cursor-pointer"
              onClick={() => setIsOpen2(!isOpen2)}
            >
              <Chevron isOpen={isOpen2} />
              <div className="fs-16 fw-700 text-black">
                {totalCentreUsersCount} Centre Users
              </div>
            </div>
            <div className="d-flex gap-3 align-items-center">
              <div className="fs-13 text-black text-nowrap">Sort By</div>
              <div className="form-group">
                <SortBySelect
                  // className="form-select text-only min-h-auto fw-700"
                  options={USER_SORT_OPTIONS}
                  onChange={(selectedValue) =>
                    handleSorting("centre_user", selectedValue)
                  }
                  enableFilter={false}
                  // value={selectedCentreUserSort}
                  defaultValue={USER_SORT_OPTIONS[0]}
                  onSelect={handleSelect}
                />
              </div>
            </div>
          </div>
          {isOpen2 && (
            <>
              {centreUsers && centreUsers?.length > 0 ? (
                centreUsers.map((centreUser: TCenterData, index: number) => (
                  <div
                    className="bg-white p-15px d-flex gap-20 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400"
                    key={index}
                  >
                    <div
                      className={`d-flex flex-column flex-lg-row gap-20 align-items-start align-items-lg-center text-break ${
                        centreUser.status === "active"
                          ? " text-black"
                          : "text-grey-medium"
                      }`}
                    >
                      <div className="min-w-200px w-200px fs-16 fw-700">
                        {capitalizeName(centreUser?.first_name ?? "")} {capitalizeName(centreUser?.last_name ?? "")}
                      </div>
                      <div className="min-w-200px w-200px">
                        {centreUser?.email}
                      </div>
                      <div className="min-w-120px">
                        {centreUser?.phone_number}
                      </div>
                      <div className="min-w-120px">
                        {centreUser.email_verified_at === null &&
                        centreUser.status === "active" ? (
                          <span className="text-teal-dark fs-13 fw-700">
                            Invitation Pending
                          </span>
                        ) : centreUser.status === "inactive" ? (
                          <span className="text-grey-medium fs-13 fw-700">
                            Inactive
                          </span>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                    <div className="position-relative">
                      <CustomButton
                        variant={"primary"}
                        className="btn-theme bg-white p-0 h-auto"
                        onClick={() => {
                          setOpenDropdown(!openDropdown);
                          setEditCentreAdminId(centreUser?.id);
                        }}
                      >
                        <VerticalDots />
                      </CustomButton>
                      {openDropdown && editCentreAdminId === centreUser?.id && (
                        <div
                          className="position-absolute end-0 d-flex flex-column dropdown-menu"
                          ref={dropdownRef} // Attach ref to handle click outside detection
                        >
                          {centreUser.email_verified_at !== null &&
                          centreUser.status === "active" ? (
                            <>
                              <div
                                className="dropdown-item cursor-pointer"
                                onClick={() => handleEdit(centreUser)}
                              >
                                <EditPencil />
                                Edit User
                              </div>
                              <div
                                className="dropdown-item cursor-pointer text-nowrap"
                                onClick={() =>
                                  makeUserUpdate(
                                    centreUser.uuid,
                                    "make_inactive"
                                  )
                                }
                              >
                                <Block />
                                Make Inactive
                              </div>
                            </>
                          ) : centreUser.email_verified_at === null &&
                            centreUser.status === "active" ? (
                            <>
                              <div
                                className="dropdown-item cursor-pointer text-nowrap"
                                onClick={() =>
                                  makeUserUpdate(
                                    centreUser.uuid,
                                    "resend_invite"
                                  )
                                }
                              >
                                <Mail />
                                Resend Invite
                              </div>
                              <div
                                className="dropdown-item cursor-pointer text-nowrap"
                                onClick={() =>
                                  makeUserUpdate(
                                    centreUser.uuid,
                                    "cancel_invite"
                                  )
                                }
                              >
                                <Block />
                                Cancel Invite
                              </div>
                            </>
                          ) : centreUser.status === "inactive" ? (
                            <>
                              <div
                                className="dropdown-item cursor-pointer text-nowrap"
                                onClick={() =>
                                  makeUserUpdate(centreUser.uuid, "make_active")
                                }
                              >
                                <Block />
                                Make Active
                              </div>
                            </>
                          ) : (
                            ""
                          )}{" "}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="d-flex flex-column align-items-center text-grey-medium">
                  No Centre User found
                </div>
              )}

              {totalCentreUsersCount > DEFAULT_ITEMS_PER_PAGE && (
                <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="fs-13 text-black text-nowrap">
                      Items per page
                    </div>
                    <div className="form-group">
                      <select
                        name=""
                        id=""
                        className="form-select text-only"
                        onChange={(e) => {
                          isInitialLoad.current = false;
                          setCentreUsersPerPageList(Number(e.target.value));
                          setCentreUsersPage(1);
                        }}
                      >
                        {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                          return (
                            <option key={index} value={item}>
                              {item}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <Pagination
                    currentPage={centreUsersPage}
                    totalPages={totalCentreUsersPages}
                    paginationLinks={centreUsersPaginationLinks}
                    maxVisiblePages={MAX_VISIBLE_PAGES}
                    onPageChange={handleCentreUserPageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CentreUsers;
